/**
 * LSystemNode - L-system string rewriting with turtle graphics parameters
 *
 * Takes an axiom and applies substitution rules N times.
 * Rules are defined via custom params where the param name is the symbol
 * to match and the value is the replacement (string splits to chars, array used as-is).
 * e.g., "F": "F-F+F" means F → [F, -, F, +, F]
 *
 * Outputs the rewritten sequence and the angle parameter for turtle interpretation.
 */

import { BaseNode } from '@nodes/BaseNode';
import { params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { Dependencies } from '@nodes/Dependencies';
import { LSYSTEM_PRESETS } from './LSystemNode.presets';
import { isMetadataDescriptor } from '@/nodes/PresetManager';

/** Convert string to char array, or use array as-is */
export function toSymbolArray(value: string|string[]): any[] {
  if (typeof value === 'string') {
    return value.split('');
  }
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
}


export const substitute = (input: string[], rules: Record<string, string|string[]>) => {
  const result: any[] = [];

  for (const item of input) {
    const key = typeof item === 'string' ? item : JSON.stringify(item);
    const replacement = rules[key];

    if (replacement !== undefined) {
      result.push(...toSymbolArray(replacement));
    } else {
      result.push(item);
    }
  }

  return result;
}
const initialPreset = LSYSTEM_PRESETS[0]
const initialRules = { ...initialPreset.params }
delete initialRules.axiom;
delete initialRules.angle;
// Filter out metadata descriptors (e.g. steps: { max: 6 }) - they're not custom rules
for (const key in initialRules) {
  if (isMetadataDescriptor(initialRules[key])) {

    delete initialRules[key];
  } else {
    initialRules[key] = { dataType: 'string', value: initialRules[key], custom: true, order: 2 };
  }
}
const lsystemEndpoints = params({
  preset: { dataType: 'string', options: LSYSTEM_PRESETS.map(p => p.name), value: initialPreset.name },
  axiom: { order: 1, dataType: 'string', label: 'Axiom', value: initialPreset.params.axiom },
  angle: { order: 2, value: initialPreset.params.angle ?? 90, min: 0, max: 360, step: 1, isOutput: true, label: 'Angle' },
  ...initialRules,
  steps: { order: 99, value: 3, min: 0, max: initialPreset.params.steps.max, step: 1, label: 'Steps' },
  out: { order: 100, dataType: 'array', isOutput: true, allowInput: false, label: 'Result' }
});


class LSystemNode extends BaseNode<typeof lsystemEndpoints> {
  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();
    this.generateOutput();
  }



  private generateOutput(): void {
    const axiom = this.params.axiom.value;
    const steps = this.params.steps.value;
    const rules = this.deps.endpointApi.getCustomParams();

    let output = toSymbolArray(axiom);

    for (let i = 0; i < steps; i++) {
      output = substitute(output, rules);
    }

    this.params.out.set(output);
  }

  onParamChanged(_payload: any, endpointId: string): void {
    if (endpointId === 'angle') return;
    this.generateOutput();
  }
}

export const lsystemNodeDef: NodeDef<LSystemNode> = {
  type: 'LSystem',
  shortName: 'lsys',
  info: 'L-system rewriting.\nAdd custom params to define rules (symbol → replacement).',
  endpoints: lsystemEndpoints,
  allowAddParam: true,
  factoryPresets: LSYSTEM_PRESETS,
  ctor: LSystemNode
};
